import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import type { AgentSessionPage } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { toast } from '@localagent/ui/components/sonner';
import { AgentDetail } from '../components/agents/AgentDetail';
import { DeleteAgentDialog, RenameAgentDialog } from '../components/agents/AgentDialogs';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import {
  AGENTS_KEY,
  useAgent,
  useAgentTeams,
  useAgentWorkspaces,
  useStartAgentSession,
} from '../hooks/useAgents';

function useAgentSessionPages(agentId: string | undefined) {
  const holdsIdentity = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const enabled = holdsIdentity && sdkConfigured && Boolean(agentId);
  return useInfiniteQuery<AgentSessionPage, Error, { pages: AgentSessionPage[] }, readonly unknown[], string | undefined>({
    queryKey: [...AGENTS_KEY, 'sessions', agentId],
    queryFn: ({ pageParam }) => {
      if (!agentId) throw new Error('agent sessions queried without an agent id');
      return getSDK().agents.sessions(agentId, { cursor: pageParam });
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
    staleTime: 0,
  });
}

export function AgentDetailView() {
  useCatalogSubscription('persona');
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const agent = useAgent(agentId);
  const sessions = useAgentSessionPages(agentId);
  const workspaces = useAgentWorkspaces(agentId);
  const teams = useAgentTeams(agentId);
  const startSession = useStartAgentSession();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const error = agent.error ?? sessions.error ?? workspaces.error ?? teams.error;

  let body;
  if (error) {
    body = (
      <p data-testid="agent-detail-error" className="text-body text-destructive">
        Failed to load this Agent: {error.message}
      </p>
    );
  } else if (!agent.data || !sessions.data || !workspaces.data || !teams.data) {
    body = (
      <p data-testid="agent-detail-loading" className="text-body text-muted-foreground">
        Loading Agent…
      </p>
    );
  } else {
    const loaded = agent.data;
    body = (
      <>
        <AgentDetail
          agent={loaded}
          sessions={sessions.data.pages.flatMap((page) => page.items)}
          hasMoreSessions={sessions.hasNextPage}
          loadingMoreSessions={sessions.isFetchingNextPage}
          onLoadMoreSessions={() => { void sessions.fetchNextPage(); }}
          workspaces={workspaces.data}
          teams={teams.data}
          newChatPending={startSession.isPending}
          onNewChat={() => {
            startSession.mutate(
              { agentId: loaded.id },
              {
                onSuccess: (session) => { void navigate(`/chat/${session.id}`); },
                onError: (err) => toast.error(err.message),
              },
            );
          }}
          onRename={() => setRenameOpen(true)}
          onDelete={() => setDeleteOpen(true)}
          onOpenSession={(sessionId, participantId) => {
            void navigate(participantId ? `/chat/${sessionId}?tab=${encodeURIComponent(participantId)}` : `/chat/${sessionId}`);
          }}
          onOpenPersona={(personaId) => { void navigate(`/personas/${personaId}`); }}
        />
        {renameOpen && (
          <RenameAgentDialog agent={loaded} open onClose={() => setRenameOpen(false)} />
        )}
        <DeleteAgentDialog
          agent={loaded}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => setDeleteOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="agent-detail-back"
            onClick={() => { void navigate('/agents?tab=agents'); }}
          >
            ← Agents
          </Button>
        </div>
        {body}
      </div>
    </div>
  );
}
